import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertCircle, Search, Loader2, Ship, ExternalLink, MapPin, ArrowRight } from 'lucide-react';
import { 
  getAllActiveVessels, 
  searchByPort,
  searchPortToPort,
  searchVesselsByName
} from '../services/VesselApiService';
import { fetchSAECSSchedules } from '../services/maerskApi';
import { fetchAmexSchedules } from '../services/AmexApi';
import { fetchMesawaSchedules } from '../services/MesawaApi';
import VesselScheduleView from './VesselScheduleView';

const VesselSearch = () => {
  const [vesselNameInput, setVesselNameInput] = useState('');
  const [originPortInput, setOriginPortInput] = useState('');
  const [destinationPortInput, setDestinationPortInput] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchType, setSearchType] = useState('vessel'); // 'vessel', 'port-to-port'
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [activeVessels, setActiveVessels] = useState([]);
  const [isLoadingVessels, setIsLoadingVessels] = useState(true);
  const [vesselListError, setVesselListError] = useState(null);
  
  const [searchHistory, setSearchHistory] = useState(() => {
    // Load search history from localStorage on component mount
    const savedHistory = localStorage.getItem('vesselSearchHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });

  // South African ports for origin selection
  const southAfricanPorts = [
    { code: 'ZADUR', name: 'Durban, South Africa' },
    { code: 'ZACPT', name: 'Cape Town, South Africa' },
    { code: 'ZAELZ', name: 'Port Elizabeth, South Africa' }
  ];

  // International ports for destination selection
  const internationalPorts = [
    { code: 'NLRTM', name: 'Rotterdam, Netherlands' },
    { code: 'GBFXT', name: 'Felixstowe, UK' },
    { code: 'BEANR', name: 'Antwerp, Belgium' },
    { code: 'SGSIN', name: 'Singapore' },
    { code: 'CNSHA', name: 'Shanghai, China' },
    { code: 'USNYC', name: 'New York, USA' },
    { code: 'USHOU', name: 'Houston, USA' },
    { code: 'JPTYO', name: 'Tokyo, Japan' }
  ];

  useEffect(() => {
    const fetchVessels = async () => {
      try {
        setVesselListError(null);
        setIsLoadingVessels(true);
        const vessels = await getAllActiveVessels();
        setActiveVessels(Array.isArray(vessels) ? vessels : []);
        if (!Array.isArray(vessels)) {
          console.warn('getAllActiveVessels did not return an array:', vessels);
        }
      } catch (err) {
        console.error('Failed to load active vessels:', err);
        setVesselListError('Failed to load vessel list. Please try refreshing.');
        setActiveVessels([]); 
      } finally {
        setIsLoadingVessels(false);
      }
    };
    fetchVessels();
  }, []); 

  // Save search history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vesselSearchHistory', JSON.stringify(searchHistory));
  }, [searchHistory]);

  useEffect(() => {
    // Clear search results and errors when switching search types
    setSearchResult(null);
    setError(null);
  }, [searchType]);

  // Memoize addToSearchHistory to make it stable
  const addToSearchHistory = useCallback((query, type) => {
    const newHistoryItem = {
      type,
      query,
      timestamp: new Date().toISOString()
    };

    // Add to search history and remove duplicates
    setSearchHistory(prevHistory => {
      // Check if this exact search already exists
      const existingItemIndex = prevHistory.findIndex(
        item => item.type === type && item.query === query
      );

      if (existingItemIndex !== -1) {
        // Move the existing item to the top (most recent) and update timestamp
        const updatedHistory = [...prevHistory];
        const existingItem = {...updatedHistory[existingItemIndex], timestamp: newHistoryItem.timestamp};
        updatedHistory.splice(existingItemIndex, 1);
        return [existingItem, ...updatedHistory].slice(0, 10); // Keep only 10 most recent
      } else {
        // Add new item to the top
        return [newHistoryItem, ...prevHistory].slice(0, 10); // Keep only 10 most recent
      }
    });
  }, [setSearchHistory]); // Dependency on setSearchHistory (stable)

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Prevent default form submission on Enter key
      e.preventDefault(); 
      // Trigger the search directly without debounce
      if (searchType === 'vessel') {
        handleSearchByVessel(vesselNameInput);
      } else if (searchType === 'port-to-port') {
        handlePortToPortSearch();
      }
    }
  };

  // Memoize the search function to be debounced
  const handleSearchByVessel = useCallback(async (vesselName) => {
    if (!vesselName) {
      setError('Please enter a Vessel Name.');
      setSearchResult(null); // Clear previous results
      return;
    }

    setError(null);
    setIsLoading(true);
    setSelectedVessel(null);
    
    try {
      console.log(`Searching for vessel: ${vesselName}`);
      // Only perform the initial search by name
      const results = await searchVesselsByName(vesselName);
      
      // Do NOT fetch service schedules here.
      // The VesselScheduleView component will fetch the detailed schedule when "View Schedule" is clicked.
      
      console.log('Initial search results:', results);
      results.searchType = 'vessel';
      setSearchResult(results);
      
      // Add to search history
      addToSearchHistory(vesselName, 'vessel');
    } catch (error) {
      console.error('Error searching vessels:', error);
      setError(`Failed to search vessels: ${error.message}`);
      setSearchResult(null); // Clear results on error
    } finally {
      setIsLoading(false);
    }
  }, [addToSearchHistory]); // Add dependencies

  // Create a debounced version of the search handler
  const debouncedSearch = useCallback(
    debounce(handleSearchByVessel, 500), // 500ms debounce delay
    [handleSearchByVessel] // Dependency array
  );

  // Effect to trigger debounced search when vesselNameInput changes
  useEffect(() => {
    if (searchType === 'vessel') {
      debouncedSearch(vesselNameInput);
    }
    // Cleanup the debounce timeout on component unmount or searchType change
    return () => {
      debouncedSearch.cancel && debouncedSearch.cancel(); // If debounce function has a cancel method
    };
  }, [vesselNameInput, searchType, debouncedSearch]);

  const handlePortToPortSearch = async () => {
    // Display "not yet working" message instead of attempting the search
    setError(null);
    setIsLoading(false);
    
    // Show "in development" message as search result
    setSearchResult({
      searchType: 'port-to-port',
      originPort: {
        code: originPortInput || 'ZADUR',
        name: originPortInput ? 
          (southAfricanPorts.find(p => p.code === originPortInput)?.name || originPortInput) : 
          'Durban, South Africa'
      },
      destinationPort: {
        code: destinationPortInput || 'NLRTM',
        name: destinationPortInput ? 
          (internationalPorts.find(p => p.code === destinationPortInput)?.name || destinationPortInput) :
          'Rotterdam, Netherlands'
      },
      routes: [],
      notYetWorking: true
    });
    
    // Still add to search history to maintain UI consistency
    if (originPortInput && destinationPortInput) {
      addToSearchHistory(`${originPortInput} → ${destinationPortInput}`, 'port-to-port');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (searchType === 'vessel') {
      // For explicit submit (e.g., clicking the button), search immediately
      handleSearchByVessel(vesselNameInput);
    } else if (searchType === 'port-to-port') {
      handlePortToPortSearch();
    }
  };

  const formatDateString = (dateStr) => {
    if (!dateStr) return 'TBD';
    
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateStr;
    }
  };

  // Helper to check if we're on a relatively small screen
  const isSmallScreen = () => {
    return window.innerWidth < 768;
  };

  const renderResults = () => {
    // If selectedVessel is set, show the vessel schedule view
    if (selectedVessel) {
      return (
        <VesselScheduleView 
          vessel={selectedVessel} 
          onBack={() => setSelectedVessel(null)} 
        />
      );
    }

    if (!searchResult) return null;

    // If this is a vessel search
    if (searchResult.searchType === 'vessel') {
      return (
        <div className="mt-6 overflow-x-auto">
          <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">
            Vessels matching: "{searchResult.searchTerm}"
          </h3>
          
          {searchResult.matches && searchResult.matches.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border dark:border-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600">Vessel Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600">IMO Number</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600">Voyage Number</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {searchResult.matches.map((vessel, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-2 whitespace-nowrap border-r dark:border-gray-600 text-gray-800 dark:text-gray-200 font-medium">
                      {vessel.vesselName || 'Unknown Vessel'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap border-r dark:border-gray-600 text-gray-800 dark:text-gray-200">
                      {vessel.vesselIMONumber || 'N/A'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap border-r dark:border-gray-600 text-gray-800 dark:text-gray-200">
                      {vessel.voyageNumber || vessel.voyage || 'N/A'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <button
                        onClick={async () => {
                          setSelectedVessel(vessel);
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex items-center"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" /> View Schedule
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded dark:bg-yellow-800 dark:border-yellow-600 dark:text-yellow-200">
              <p>No vessels found matching "{searchResult.searchTerm}".</p>
            </div>
          )}
        </div>
      );
    } else if (searchResult.searchType === 'port-to-port') {
      return (
        <div className="mt-6 overflow-x-auto">
          <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">
            Routes from {searchResult.originPort.name} to {searchResult.destinationPort.name}
          </h3>
          
          {searchResult.notYetWorking ? (
            <div className="mt-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded dark:bg-yellow-800 dark:border-yellow-600 dark:text-yellow-200">
              <h4 className="font-semibold text-lg mb-2">Feature in Development</h4>
              <p>The Port-to-Port search functionality is currently under development. We're working to bring you accurate route information between ports soon.</p>
              <div className="mt-3 p-2 bg-white dark:bg-gray-700 rounded border border-yellow-300 dark:border-yellow-700">
                <p className="text-gray-700 dark:text-gray-300 font-medium">Development Status:</p>
                <ul className="list-disc ml-5 mt-1 text-sm">
                  <li className="text-green-600 dark:text-green-400">✓ API integration complete</li>
                  <li className="text-green-600 dark:text-green-400">✓ Data processing logic implemented</li>
                  <li className="text-red-600 dark:text-red-400">× Reliability testing in progress</li>
                  <li className="text-red-600 dark:text-red-400">× Error handling refinement needed</li>
                </ul>
              </div>
            </div>
          ) : searchResult.routes && searchResult.routes.length > 0 ? (
            <div>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-300 text-blue-800 rounded dark:bg-blue-900 dark:border-blue-800 dark:text-blue-200">
                Found {searchResult.routes.length} {searchResult.routes.length === 1 ? 'route' : 'routes'} from {searchResult.originPort.name} to {searchResult.destinationPort.name}.
              </div>
              
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600">Vessel</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600">Service</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600">Departure</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r dark:border-gray-600">Arrival</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Transit Days</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {searchResult.routes.map((route, index) => (
                    <tr key={index} className={route.vessel.serviceCode === '160' ? 'bg-green-50 hover:bg-green-100 dark:bg-green-900 dark:hover:bg-green-800' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}>
                      <td className="px-4 py-2 whitespace-nowrap border-r dark:border-gray-600 text-gray-800 dark:text-gray-200 font-medium">
                        {route.vessel.name || 'Unknown Vessel'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap border-r dark:border-gray-600 text-gray-800 dark:text-gray-200">
                        {route.vessel.serviceCode === '160' ? 
                          <span className="font-medium text-green-700 dark:text-green-400">Safari Service</span> : 
                          (route.vessel.service || 'Unknown Service')}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap border-r dark:border-gray-600 text-gray-800 dark:text-gray-200">
                        {typeof route.origin.departureDate === 'string' && route.origin.departureDate !== 'TBD' ? 
                          formatDateString(route.origin.departureDate) : 'TBD'}
                        {route.origin.isActualDeparture && 
                          <span className="ml-1 text-xs bg-green-100 text-green-800 px-1 rounded dark:bg-green-900 dark:text-green-200">Actual</span>
                        }
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap border-r dark:border-gray-600 text-gray-800 dark:text-gray-200">
                        {typeof route.destination.arrivalDate === 'string' && route.destination.arrivalDate !== 'TBD' ? 
                          formatDateString(route.destination.arrivalDate) : 'TBD'}
                        {route.destination.isActualArrival && 
                          <span className="ml-1 text-xs bg-green-100 text-green-800 px-1 rounded dark:bg-green-900 dark:text-green-200">Actual</span>
                        }
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-800 dark:text-gray-200">
                        {typeof route.transitDays === 'number' ? `${route.transitDays} days` : route.transitDays}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded dark:bg-yellow-800 dark:border-yellow-600 dark:text-yellow-200">
              <p>No direct routes found between {searchResult.originPort.name} and {searchResult.destinationPort.name}.</p>
              <p className="mt-1 text-sm">Try selecting different ports or checking for connecting services.</p>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Maersk Vessel Tracker</h1>
      
      {/* Search type tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="text-sm text-gray-500 dark:text-gray-400 mr-1">Search by:</div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSearchType('vessel')}
            className={`px-3 py-2 rounded-md flex items-center ${
              searchType === 'vessel'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white'
            }`}
          >
            <Ship className="h-4 w-4 mr-2" />
            Vessel Name
          </button>
          <button
            type="button"
            onClick={() => setSearchType('port-to-port')}
            className={`px-3 py-2 rounded-md flex items-center ${
              searchType === 'port-to-port'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white'
            }`}
          >
            <MapPin className="h-4 w-4 mr-2" />
            Port to Port
            <span className="ml-2 text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">Not yet working</span>
          </button>
        </div>
      </div>
      
      {/* Search form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <div className={`grid gap-3 ${searchType === 'port-to-port' ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {searchType === 'vessel' ? (
            <input 
              type="text"
              value={vesselNameInput}
              onChange={(e) => {
                setVesselNameInput(e.target.value);
                // Debounced search is triggered by the useEffect hook
              }}
              onKeyPress={handleKeyPress}
              placeholder="Enter Vessel Name (e.g., Maersk)"
              className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none md:col-span-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              disabled={isLoadingVessels || !!vesselListError}
            />
          ) : (
            <>
              <div className="flex flex-col">
                <label className="mb-1 text-sm text-gray-600 dark:text-gray-300">Origin (South Africa)</label>
                <select
                  value={originPortInput}
                  onChange={(e) => setOriginPortInput(e.target.value)}
                  className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select origin port</option>
                  {southAfricanPorts.map((port, index) => (
                    <option key={index} value={port.code}>
                      {port.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col">
                <label className="mb-1 text-sm text-gray-600 dark:text-gray-300">Destination</label>
                <select
                  value={destinationPortInput}
                  onChange={(e) => setDestinationPortInput(e.target.value)}
                  className="p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select destination port</option>
                  {internationalPorts.map((port, index) => (
                    <option key={index} value={port.code}>
                      {port.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          
          <button
            onClick={handleSubmit}
            className={`p-2 rounded text-white flex items-center justify-center transition duration-150 ease-in-out ${
              isLoading || (searchType === 'vessel' && (isLoadingVessels || !!vesselListError))
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            disabled={isLoading || (searchType === 'vessel' && (isLoadingVessels || !!vesselListError))}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-5 w-5 mr-2" />
                Search
              </>
            )}
          </button>
        </div>
        
        {searchType === 'vessel' && vesselListError && (
          <div className="mt-2 text-sm text-red-600 dark:text-red-400">
            {vesselListError}
          </div>
        )}
        
        {/* Recent searches */}
        {!isLoading && searchHistory.length > 0 && (
          <div className="mt-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">Recent searches:</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {searchHistory
                .filter(item => item.type === searchType)
                .slice(0, 5)
                .map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      if (item.type === 'vessel') {
                        // Set the vessel name first
                        setVesselNameInput(item.query);
                        // Set search type to vessel
                        setSearchType('vessel');
                        // Reset any previously selected vessel
                        setSelectedVessel(null);
                        // Then perform a direct search without using the handler
                        setIsLoading(true);
                        setError(null);
                        
                        // Use the full search handler instead of direct API call
                        // This ensures voyage information is fetched properly
                        handleSearchByVessel(item.query); // Pass the query to the handler
                      } else if (item.type === 'port-to-port') {
                        setSearchType('port-to-port');
                        // Reset any previously selected vessel
                        setSelectedVessel(null);
                        const [origin, destination] = item.query.split(' → ');
                        setOriginPortInput(origin);
                        setDestinationPortInput(destination);
                        
                        // Call port-to-port search directly
                        handlePortToPortSearch();
                      }
                    }}
                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded flex items-center dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300"
                  >
                    {item.type === 'vessel' ? (
                      <Ship className="mr-1 h-3 w-3" />
                    ) : (
                      <MapPin className="mr-1 h-3 w-3" />
                    )}
                    {item.query}
                  </button>
                ))}
            </div>
          </div>
        )}
      </form>
      
      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start dark:bg-red-900 dark:border-red-800 dark:text-red-200">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      
      {/* Search results - only show relevant results for the current search type */}
      {searchResult && searchResult.searchType === searchType && renderResults()}
      
      {/* Active vessels info */}
      {activeVessels.length > 0 && (
        <div className="mt-8 p-4 border border-gray-200 rounded bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Active Vessels</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Currently tracking {activeVessels.length} active vessels in the Maersk fleet.</p>
        </div>
      )}
    </div>
  );
};

export default VesselSearch;
